import { useEffect } from 'react';

/**
 * The two things that make an overlay *modal* over the page rather than a panel drawn on top of it:
 * Escape dismisses it, and the page behind it does not scroll.
 *
 * The lock goes on `document.body`, not on the overlay. A wheel gesture over a full-height overlay
 * should move the overlay's own content, and it was moving the article list underneath instead —
 * the overlay's scroll box is often barely taller than the viewport, so a gesture reaches its end
 * almost immediately and chains out to the document. `body { overflow: hidden }` propagates to the
 * viewport (the root element is `visible`, so the viewport takes body's value), which stops the
 * chained scroll; `overscroll-behavior: contain` on the overlay's own scroll box stops the gesture
 * one step earlier.
 *
 * Focus is the third piece of the same job and lives in `useFocusTrap`, kept separate because its
 * declaration order relative to the effect that moves focus into the surface is load-bearing.
 *
 * @param active whether the surface is on screen and modal.
 * @param onClose dismiss handler; Escape is ignored when the caller supplies none.
 */
export function useModalSurface(active: boolean, onClose?: () => void): void {
  useEffect(() => {
    if (!active || !onClose) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, onClose]);

  useEffect(() => {
    if (!active) {
      return;
    }
    // Restores whatever was there rather than clearing it: another surface may hold its own lock.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [active]);
}
